import type { ReactElement, ReactNode } from 'react';
import { Link as AriaLink, type LinkProps as AriaLinkProps } from 'react-aria-components';
import cx from 'clsx';
import {
  type ButtonStyleProps,
  getButtonClassNames,
  resolveButtonVariantProps,
} from '#components/button/button';
import externalLinkStyles from './external-link.module.css';

export interface ExternalLinkProps extends AriaLinkProps, ButtonStyleProps {
  display?: 'link' | 'button';
  label?: string;
  children?: ReactNode;
}

export function ExternalLink({
  children,
  label,
  display = 'link',
  className,
  target = '_blank',
  rel = 'noopener noreferrer',
  variant,
  size,
  iconOnly = false,
  stretch = false,
  iconLeft,
  iconRight,
  ...props
}: ExternalLinkProps): ReactElement {
  const isButtonDisplay = display === 'button';
  let classes = cx('body-standard-link', externalLinkStyles.link, className);
  let resolvedIconLeft: ReactNode;
  let resolvedIconRight: ReactNode;

  if (isButtonDisplay) {
    const { effectiveVariant, effectiveIconLeft, effectiveIconRight, effectiveSize } =
      resolveButtonVariantProps({ variant, iconOnly, iconLeft, iconRight, size });
    classes = getButtonClassNames({
      variant: effectiveVariant,
      size: effectiveSize,
      iconOnly,
      stretch,
      className: typeof className === 'string' ? className : undefined,
    });
    resolvedIconLeft = effectiveIconLeft;
    resolvedIconRight = effectiveIconRight;
  }

  return (
    <AriaLink className={classes} target={target} rel={rel} {...props}>
      {isButtonDisplay ? (
        <>
          {resolvedIconLeft}
          {label ?? children}
          {resolvedIconRight}
        </>
      ) : (
        (label ?? children)
      )}
    </AriaLink>
  );
}
